import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { portal, portalTokenSet } from "../../lib/api";
import { applyBranding } from "../../lib/auth";

export default function PortalVerify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [error, setError] = useState(null);
  // React 18 StrictMode intentionally double-invokes effects in dev. Magic-link
  // tokens are single-use, so a second fire would 401 and show "invalid link"
  // to a user who successfully signed in. This ref makes the effect body run
  // exactly once for the lifetime of the component.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;
    const token = params.get("token");
    // Direct-token mode: `?jwt=<client_jwt>` skips the single-use magic-link
    // exchange and just seats the pre-minted client JWT into localStorage.
    // Used for internal / admin-generated reusable preview links; the
    // regular client email flow still uses `?token=` (magic link).
    const directJwt = params.get("jwt");
    if (directJwt) {
      portalTokenSet(directJwt);
      // Hydrate branding + client/agent info from /portal/me so the header
      // renders correctly without needing the token-exchange payload.
      portal.get("/portal/me").then(({ data }) => {
        if (data?.agent) {
          localStorage.setItem("realtor_os_portal_agent", JSON.stringify(data.agent));
          localStorage.setItem("realtor_os_portal_brand", data.agent.brand_color || "#1F6FEB");
          applyBranding(data.agent.brand_color, data.agent.accent_color);
          document.documentElement.style.setProperty("--primary-hex", data.agent.brand_color || "#1F6FEB");
        }
        if (data?.client) localStorage.setItem("realtor_os_portal_client", JSON.stringify(data.client));
        if (data?.brokerage) localStorage.setItem("realtor_os_portal_brokerage", JSON.stringify(data.brokerage));
        if (data?.disclosure) localStorage.setItem("realtor_os_portal_disclosure", JSON.stringify(data.disclosure));
        nav("/portal", { replace: true });
      }).catch(err => setError(err?.response?.data?.detail || "Token is invalid or expired"));
      return;
    }
    if (!token) { setError("No token in URL"); return; }
    // Use `portal` axios so a 401 sends to /portal/expired, not /login.
    portal.post("/portal/verify", { token }).then(({ data }) => {
      portalTokenSet(data.access_token);
      localStorage.setItem("realtor_os_portal_brand", data.agent.brand_color || "#1F6FEB");
      localStorage.setItem("realtor_os_portal_agent", JSON.stringify(data.agent));
      localStorage.setItem("realtor_os_portal_client", JSON.stringify(data.client));
      if (data.brokerage) {
        localStorage.setItem("realtor_os_portal_brokerage", JSON.stringify(data.brokerage));
      } else {
        localStorage.removeItem("realtor_os_portal_brokerage");
      }
      if (data.disclosure) {
        localStorage.setItem("realtor_os_portal_disclosure", JSON.stringify(data.disclosure));
      } else {
        localStorage.removeItem("realtor_os_portal_disclosure");
      }
      const root = document.documentElement;
      applyBranding(data.agent.brand_color, data.agent.accent_color);
      // Also keep the legacy CSS-var write for any consumer still reading it directly.
      root.style.setProperty("--primary-hex", data.agent.brand_color || "#1F6FEB");
      nav("/portal", { replace: true });
    }).catch(err => setError(err?.response?.data?.detail || "Link is invalid or expired"));
  }, [params, nav]);

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-center max-w-sm px-6">
        {error ? (
          <>
            <div className="font-heading text-2xl font-bold text-slate-900" data-testid="portal-verify-error">Something's not right</div>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <p className="mt-4 text-xs text-slate-500">Ask your agent to send you a fresh link.</p>
          </>
        ) : (
          <div className="text-sm text-slate-500">Signing you in…</div>
        )}
      </div>
    </div>
  );
}
